import { defineStore } from "pinia";
import _ from "lodash";
import router from "@/employee/routes";
import {
  BalanceTransferMethod,
  BalanceTransferState,
  BalanceTransferStepKey,
  FetchState,
  RolloverBalanceTransferFormRequest,
  TrusteeToTrusteeBalanceTransferFormRequest,
  TrusteeToTrusteeBalanceTransferSignatureSaveRequest,
  TrusteeToTrusteeBalanceTransferSigningModelRequest,
  TrusteeToTrusteeBalanceTransferSigningModelResponse
} from "@/shared/types";
import {
  generateRolloverBalanceTransferForm,
  generateTrusteeToTrusteeBalanceTransferForm,
  getBalanceTransferCustodians,
  getTrusteeToTrusteeBalanceTransferSigningModel,
  signTrusteeToTrusteeBalanceTransferForm
} from "@/shared/api/userService";
import * as storeHelper from "@/shared/storeHelper";
import {
  OnboardingBalanceTransfer as OnboardingBalanceTransferRouteName,
  Transfers as TransfersRouteName
} from "@/employee/route-names";
import { useUserBankAccountStore } from "@/shared/store/userBankAccount";

export const initialState: BalanceTransferState = {
  transferMethod: BalanceTransferMethod.None,
  rolloverDetails: {
    amount: 0
  },
  trusteeToTrusteeDetails: {
    trusteeAccountNumber: "",
    transferEntireBalance: true,
    transferPartialBalance: false,
    amount: 0
  },
  isOnboarding: false,
  custodiansFetchState: FetchState.Uninitialized,
  custodians: [],
  selectedCustodian: "",
  isCustodianUnknown: false,
  isAccountNumberUnknown: false,
  eSignModel: {
    requestId: 0,
    documentUrl: ""
  }
};

export const useBalanceTransferStore = defineStore("balanceTransfer", {
  state: () => (_.cloneDeep(initialState)),
  getters: {
    transferBalanceOptionSelected: state => state.trusteeToTrusteeDetails.transferEntireBalance || state.trusteeToTrusteeDetails.transferPartialBalance,
    canESign: state => !state.isCustodianUnknown && !state.isAccountNumberUnknown,
    eSignModelLoaded: state => state.eSignModel && state.eSignModel.documentUrl?.length > 0 && state.eSignModel.requestId > 0
  },
  actions: {
    setESignModel (model: TrusteeToTrusteeBalanceTransferSigningModelResponse) {
      this.eSignModel = model;
    },
    generateTrusteeToTrusteeBalanceTransferForm () {
      let transferEntireBalance: boolean = null;

      if (this.trusteeToTrusteeDetails.transferEntireBalance) {
        transferEntireBalance = true;
      } else if (this.trusteeToTrusteeDetails.transferPartialBalance) {
        transferEntireBalance = false;
      }

      const trusteeAccountNumber = this.isAccountNumberUnknown
        ? ""
        : this.trusteeToTrusteeDetails.trusteeAccountNumber;

      const userBankAccountStore = useUserBankAccountStore();
      const request: TrusteeToTrusteeBalanceTransferFormRequest = {
        trusteeAccountNumber,
        transferEntireBalance,
        amount: this.trusteeToTrusteeDetails.amount > 0 ? this.trusteeToTrusteeDetails.amount : null,
        hsaBankAccountNumber: userBankAccountStore.accountNumber
      };

      return generateTrusteeToTrusteeBalanceTransferForm(request)
        .then(response => response.data)
        .catch(error => storeHelper.handleError({ error, rethrow: true }));
    },
    generateRolloverBalanceTransferForm () {
      const userBankAccountStore = useUserBankAccountStore();
      const request: RolloverBalanceTransferFormRequest = {
        amount: this.rolloverDetails.amount > 0 ? this.rolloverDetails.amount : null,
        hsaBankAccountNumber: userBankAccountStore.accountNumber
      };

      return generateRolloverBalanceTransferForm(request)
        .then(response => response.data)
        .catch(error => storeHelper.handleError({ error, rethrow: true }));
    },
    getTrusteeToTrusteeBalanceTransferSigningModel () {
      let transferEntireBalance: boolean = null;

      if (this.trusteeToTrusteeDetails.transferEntireBalance) {
        transferEntireBalance = true;
      } else if (this.trusteeToTrusteeDetails.transferPartialBalance) {
        transferEntireBalance = false;
      }

      const userBankAccountStore = useUserBankAccountStore();
      const request: TrusteeToTrusteeBalanceTransferSigningModelRequest = {
        trusteeCustodianKey: this.selectedCustodian,
        trusteeAccountNumber: this.trusteeToTrusteeDetails.trusteeAccountNumber,
        transferEntireBalance,
        amount: this.trusteeToTrusteeDetails.amount > 0 ? this.trusteeToTrusteeDetails.amount : null,
        hsaBankAccountNumber: userBankAccountStore.accountNumber
      };

      return getTrusteeToTrusteeBalanceTransferSigningModel(request)
        .then(response => this.setESignModel(response.data))
        .catch(error => storeHelper.handleError({ error, rethrow: true }));
    },
    signTrusteeToTrusteeBalanceTransferForm (request: TrusteeToTrusteeBalanceTransferSignatureSaveRequest): Promise<any> {
        return new Promise<any>((resolve, reject) => {
            return signTrusteeToTrusteeBalanceTransferForm(request).then(resp => {
                return resolve(resp.data);
            }).catch(err => {
                storeHelper.handleError({ error: err, rethrow: true });
                return reject();
            });
        });
    },
    clearState () {
      this.transferMethod = BalanceTransferMethod.None;
      this.rolloverDetails = { amount: 0 };
      this.trusteeToTrusteeDetails = {
        trusteeAccountNumber: "",
        transferEntireBalance: true,
        transferPartialBalance: false,
        amount: 0
      };
      this.selectedCustodian = "";
      this.isCustodianUnknown = false;
      this.isAccountNumberUnknown = false;
      this.resetESign();
    },
    resetESign () {
      this.eSignModel = {
        requestId: 0,
        url: ""
      };
    },
    goToStep (stepKey?: BalanceTransferStepKey) {
      const routeName = this.isOnboarding ? OnboardingBalanceTransferRouteName : TransfersRouteName;

      return router.push({
        name: routeName,
        params: {
          stepKey
        }
      });
    },
    goToStart () {
      this.goToStep();
    },
    loadCustodians () {
      if (this.custodiansFetchState !== FetchState.Uninitialized) {
        return;
      }

      this.custodiansFetchState = FetchState.Loading;

      getBalanceTransferCustodians()
          .then(response => {
            this.custodians = response.data.custodians;
            this.custodiansFetchState = FetchState.Loaded;
          })
          .catch(error => {
            this.custodiansFetchState = FetchState.Failed;
            return storeHelper.handleError({ error, rethrow: false });
          });
    }
  }
});
